/**
 * Cross-border payment rail pricing.
 *
 * Compares the cost of collecting a USD invoice/shipment value through the
 * payment rails available to an Indian exporter:
 *  - Karbon Forex     — SWIFT wire facilitator via JP Morgan USA, NOT a PA-CB (no RBI cap)
 *  - Skydo            — RBI-licensed Payment Aggregator - Cross Border (PA-CB)
 *  - Razorpay          — RBI-licensed PA-CB (Export Account)
 *  - Traditional bank wire — manual SWIFT via an AD Category-I bank, no cap but slow/expensive
 *
 * RBI's PA-CB framework caps a single PA-CB transaction at ₹25,00,000 (~USD 29,000
 * at commonly-used reference rates). Above that threshold, Skydo and Razorpay
 * cannot legally process the transaction and the exporter must fall back to a
 * traditional AD bank wire (or Karbon, which operates outside the PA-CB regime).
 *
 * Fee formulas below were fact-checked against each vendor's public pricing at
 * the time this module was written.
 */

/** RBI PA-CB per-transaction cap, in USD (~₹25,00,000). */
export const PACB_CAP_USD = 29000;

/** Stable identifier for each supported payment rail. */
export type PaymentRailKey = 'karbon' | 'skydo' | 'razorpay' | 'wire';

export interface PaymentRailQuote {
  /** Stable key for the rail, used for lookups/keys in UI lists. */
  key: PaymentRailKey;
  /** Display name, e.g. "Skydo". */
  name: string;
  /** Short description of the rail's regulatory category. */
  type: string;
  /** Total fee charged by the rail (in USD), inclusive of GST where applicable. */
  fee: number;
  /** Additional value lost to FX markup/spread (in USD). 0 where the rail advertises 0% FX markup. */
  fxLoss: number;
  /** Human-readable settlement speed, e.g. "< 24 hours". */
  speed: string;
  /** True when the invoice value exceeds the RBI PA-CB cap and this rail cannot legally process it. */
  capped: boolean;
  /** Short explanatory note shown in the UI (FIRC/FIRA behavior, cap details, etc). */
  note: string;
}

/**
 * Compute a fee/FX quote for every supported cross-border collection rail at
 * a given invoice value (in USD).
 *
 * Faithful TypeScript port of the validated `computePaymentRails` prototype —
 * the fee math is intentionally unchanged from the prototype that was
 * fact-checked against vendor pricing pages.
 */
export function computePaymentRails(valueUsd: number): PaymentRailQuote[] {
  const rails: PaymentRailQuote[] = [];

  // Karbon Forex — SWIFT wire facilitator via JP Morgan USA, not a PA-CB.
  // 1% transaction fee + 18% GST on the fee, 0% FX markup, no stated cap.
  const karbonFee = valueUsd * 0.01 * 1.18;
  rails.push({
    key: 'karbon',
    name: 'Karbon Forex',
    type: 'SWIFT wire facilitator (JP Morgan USA) — not a PA-CB',
    fee: karbonFee,
    fxLoss: 0,
    speed: '~1 business day',
    capped: false,
    note: 'FIRC issued automatically. No stated per-transaction cap.',
  });

  // Skydo — RBI-licensed PA-CB. Tiered fee + 18% GST, 0% FX margin, ₹25L cap applies.
  let skydoFeeBase: number;
  if (valueUsd <= 2000) {
    skydoFeeBase = 19;
  } else if (valueUsd <= 10000) {
    skydoFeeBase = 29;
  } else {
    skydoFeeBase = valueUsd * 0.003; // 0.3% above $10,000
  }
  const skydoFee = skydoFeeBase * 1.18;
  rails.push({
    key: 'skydo',
    name: 'Skydo',
    type: 'RBI-licensed PA-CB',
    fee: skydoFee,
    fxLoss: 0,
    speed: '< 24 hours',
    capped: valueUsd > PACB_CAP_USD,
    note: 'Instant FIRA. Cheapest option roughly $2K–$29K.',
  });

  // Razorpay — RBI-licensed PA-CB (export account), ~1% + GST, ₹25L cap applies.
  const razorpayFee = valueUsd * 0.01 * 1.18;
  rails.push({
    key: 'razorpay',
    name: 'Razorpay (Export Account)',
    type: 'RBI-licensed PA-CB',
    fee: razorpayFee,
    fxLoss: 0,
    speed: 'Same day – 1 business day',
    capped: valueUsd > PACB_CAP_USD,
    note: 'PA-CB license since Dec 2025. ₹25L (~$29K) per-transaction cap applies.',
  });

  // Traditional bank wire — no cap, but highest cost + slowest.
  const wireFee = Math.max(30, valueUsd * 0.03);
  const wireFxLoss = valueUsd * 0.03;
  rails.push({
    key: 'wire',
    name: 'Traditional bank wire (AD bank)',
    type: 'Manual SWIFT via AD Category-I bank',
    fee: wireFee,
    fxLoss: wireFxLoss,
    speed: '3–5 business days',
    capped: false,
    note: 'No cap — required above ₹25L regardless of rail chosen. Highest cost, slowest settlement.',
  });

  return rails;
}

/**
 * Select the recommended rail: filter out any rail blocked by the RBI PA-CB
 * cap, then pick the lowest (fee + fxLoss) among what remains.
 *
 * Returns `undefined` only if `rails` is empty (should not happen for real
 * invoice values, since the traditional bank wire is never capped).
 */
export function getCheapestRail(rails: PaymentRailQuote[]): PaymentRailQuote | undefined {
  const eligible = rails.filter((rail) => !rail.capped);
  if (eligible.length === 0) {
    return undefined;
  }

  return eligible.reduce((cheapest, rail) =>
    rail.fee + rail.fxLoss < cheapest.fee + cheapest.fxLoss ? rail : cheapest
  );
}

/** Total cost (fee + FX loss) an exporter would give up on a given rail. */
export function getTotalCost(rail: PaymentRailQuote): number {
  return rail.fee + rail.fxLoss;
}

/** Net amount the exporter would actually receive after fees and FX loss. */
export function getNetReceived(valueUsd: number, rail: PaymentRailQuote): number {
  return Math.max(valueUsd - getTotalCost(rail), 0);
}
